import { getElementBounds } from './elementBounds';

/**
 * Pixelate region from source canvas into target ctx at (dx,dy) with size (dw,dh).
 */
export function drawPixelatedRegion(destCtx, sourceCanvas, sx, sy, sw, sh, blockSize) {
  if (sw < 1 || sh < 1) return;
  const bs = Math.max(2, Math.round(blockSize));
  const tmp = document.createElement('canvas');
  const tw = Math.max(1, Math.ceil(sw / bs));
  const th = Math.max(1, Math.ceil(sh / bs));
  tmp.width = tw;
  tmp.height = th;
  const tctx = tmp.getContext('2d');
  if (!tctx) return;
  tctx.imageSmoothingEnabled = false;
  tctx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, tw, th);
  destCtx.imageSmoothingEnabled = false;
  destCtx.drawImage(tmp, 0, 0, tw, th, sx, sy, sw, sh);
  destCtx.imageSmoothingEnabled = true;
}

/**
 * Dim outside region + dashed crop marquee (draft overlay).
 */
export function drawCropDraft(ctx, d) {
  if (!d || d.type !== 'crop' || d.w < 1 || d.h < 1) return;
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.save();
  ctx.fillStyle = 'rgba(15, 23, 42, 0.5)';
  ctx.fillRect(0, 0, W, H);
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillRect(d.x, d.y, d.w, d.h);
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.setLineDash([10, 6]);
  ctx.strokeRect(d.x + 0.5, d.y + 0.5, d.w, d.h);
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.9)';
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.strokeRect(d.x + 1.5, d.y + 1.5, d.w - 2, d.h - 2);
  ctx.restore();
}

function drawArrowHead(ctx, x1, y1, x2, y2, size) {
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const a1 = ang + Math.PI * 0.85;
  const a2 = ang - Math.PI * 0.85;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 + Math.cos(a1) * size, y2 + Math.sin(a1) * size);
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 + Math.cos(a2) * size, y2 + Math.sin(a2) * size);
  ctx.stroke();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLCanvasElement | null} baseCanvas
 * @param {object[]} elements
 * @param {{ selectedId?: string | null; draft?: object | null; editingTextId?: string | null }} opts
 */
export function drawElements(ctx, baseCanvas, elements, opts = {}) {
  const { selectedId = null, draft = null, editingTextId = null } = opts;
  const list = draft ? [...elements, draft] : elements;

  for (const el of list) {
    if (!el || el.type === 'draft') continue;
    switch (el.type) {
      case 'rect': {
        ctx.save();
        ctx.strokeStyle = el.color;
        ctx.lineWidth = el.strokeWidth;
        ctx.setLineDash([]);
        ctx.strokeRect(el.x + 0.5, el.y + 0.5, el.w, el.h);
        ctx.restore();
        break;
      }
      case 'arrow': {
        ctx.save();
        ctx.strokeStyle = el.color;
        ctx.lineWidth = el.strokeWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(el.x1, el.y1);
        ctx.lineTo(el.x2, el.y2);
        ctx.stroke();
        const head = Math.max(10, el.strokeWidth * 4);
        drawArrowHead(ctx, el.x1, el.y1, el.x2, el.y2, head);
        ctx.restore();
        break;
      }
      case 'text': {
        if (editingTextId && el.id === editingTextId) break;
        ctx.save();
        ctx.fillStyle = el.color;
        ctx.font = `${el.fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
        ctx.textBaseline = 'top';
        const lines = String(el.text || '').split('\n');
        let ly = el.y;
        for (const line of lines) {
          ctx.fillText(line, el.x, ly);
          ly += el.fontSize * 1.25;
        }
        ctx.restore();
        break;
      }
      case 'blur': {
        if (baseCanvas) {
          ctx.save();
          drawPixelatedRegion(ctx, baseCanvas, el.x, el.y, el.w, el.h, el.blockSize ?? 12);
          ctx.restore();
        }
        break;
      }
      case 'path': {
        if (!el.points || el.points.length < 2) break;
        ctx.save();
        ctx.strokeStyle = el.color;
        ctx.lineWidth = el.strokeWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(el.points[0].x, el.points[0].y);
        for (let i = 1; i < el.points.length; i++) {
          ctx.lineTo(el.points[i].x, el.points[i].y);
        }
        ctx.stroke();
        ctx.restore();
        break;
      }
      case 'crop': {
        drawCropDraft(ctx, el);
        break;
      }
      default:
        break;
    }
  }

  if (selectedId) {
    const el = elements.find((e) => e.id === selectedId);
    if (el) {
      const b = getElementBounds(el, ctx);
      drawSelectionHighlight(ctx, b.x, b.y, b.w, b.h);
      drawCornerHandles(ctx, b.x, b.y, b.w, b.h);
    }
  }
}

function drawSelectionHighlight(ctx, x, y, w, h) {
  ctx.save();
  ctx.fillStyle = 'rgba(59, 130, 246, 0.07)';
  ctx.fillRect(x, y, w, h);
  ctx.restore();
}

function drawCornerHandles(ctx, x, y, w, h) {
  const hs = Math.max(7, Math.min(w, h) * 0.05);
  ctx.save();
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.95)';
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 4]);
  ctx.strokeRect(x + 0.5, y + 0.5, w, h);
  ctx.setLineDash([]);
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = '#3b82f6';
  const pts = [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ];
  for (const [px, py] of pts) {
    ctx.beginPath();
    ctx.rect(px - hs / 2, py - hs / 2, hs, hs);
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Composite base + elements to a new canvas (full resolution).
 * @param {HTMLCanvasElement} baseCanvas
 * @param {object[]} elements
 */
export function compositeToCanvas(baseCanvas, elements) {
  const out = document.createElement('canvas');
  out.width = baseCanvas.width;
  out.height = baseCanvas.height;
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.drawImage(baseCanvas, 0, 0);
  drawElements(ctx, baseCanvas, elements, { selectedId: null });
  return out;
}

export async function copyCanvasToClipboard(canvas) {
  if (!navigator.clipboard || !window.ClipboardItem) {
    throw new Error('Clipboard API not available');
  }
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
}

export function downloadCanvasPng(canvas, filename = 'marked-image.png') {
  const url = canvas.toDataURL('image/png');
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
}

// Re-export for consumers that only imported from draw.js
export { measureTextBounds } from './elementBounds';
